import { MoreHorizontal } from 'lucide-react'
import type { SyntheticEvent } from 'react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Skeleton,
  Switch,
} from '@teach/ui'
import type { LanguageState } from '@/features/ai/studioStartTypes'
import type { ContentLang } from '../../editorSlice'
import { LanguageFlag } from '@/components/layout/sidebar/LanguageFlag'
import { LANG_LABEL } from '../../lib/contentLangLabels'
import { useMessages } from '@/i18n'
import { messages } from './messages'
import { LangStatusChip } from './LangStatusChip'
import { DisabledHint } from './translation/DisabledHint'
import { BaseLangBadge } from './BaseLangBadge'
import { LangVisibilitySwitch } from './LangVisibilitySwitch'

interface Props {
  id: ContentLang
  isBase: boolean
  /** Строка `languages[]` сметы; `null` — смета не доехала или старый бэк. */
  state: LanguageState | null
  /** Сервер пересчитывает языки после смены базового: на месте статуса скелетон. */
  statusLoading?: boolean
  courseId: number
  /** Язык показан ученикам (`isTranslationPublished`) — положение тумблера. */
  isPublished: boolean
  /** Пункты «⋯» погашены: идёт свой прогон или летит языковое действие (сервер
   *  ответил бы `409`), либо курс на проверке (контент заморожен до решения). */
  actionsDisabled: boolean
  /** Почему погашены пункты «⋯», если причину стоит назвать. */
  actionsHint?: string | null
  /** Тумблер погашен: летит языковое действие или курс на проверке. Прогону он не мешает. */
  switchDisabled: boolean
  onMakeBase: () => void
  /** Язык ещё не включён: строка с выключенным тумблером без статуса и «Базовый». */
  enabled?: boolean
  onEnable?: () => void
  /** Есть что удалять на сервере: корзина появляется при наведении на строку. */
  onDelete?: () => void
}

// Клики по кнопке и тумблеру внутри пункта меню не должны выбирать язык и закрывать меню.
const stop = (e: SyntheticEvent) => {
  e.stopPropagation()
  e.preventDefault()
}
const stopMenu = {
  onPointerDown: stop,
  onPointerUp: stop,
  onClick: stop,
  onKeyDown: (e: SyntheticEvent) => e.stopPropagation(),
}

/**
 * Строка меню языка: у базового — «Базовый» и точка «курс наполнен не до
 * конца», у перевода — чип статуса второй строкой и два действия. Смена
 * базового больше не лочится первым переводом (§6.10): прежний базовый
 * становится переводом, и текст никуда не переезжает.
 */
export const LangMenuItem = ({
  id,
  isBase,
  state,
  statusLoading = false,
  courseId,
  isPublished,
  actionsDisabled,
  actionsHint = null,
  switchDisabled,
  onMakeBase,
  enabled = true,
  onEnable,
  onDelete,
}: Props) => {
  const m = useMessages(messages)
  return (
    <>
      <DropdownMenuItem
        // Здесь только языки и перевод: язык просмотра выбирается не в этом меню.
        onSelect={(e) => e.preventDefault()}
        className="my-1 cursor-default gap-3 px-3 py-3"
        data-testid={enabled ? `lang-item-${id}` : `lang-item-${id}-off`}
      >
        <LanguageFlag lang={id} className="size-7 shrink-0" />
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="flex items-center gap-2">
            <span className="truncate">{LANG_LABEL[id]}</span>
          </span>
          {/* Макет: статус — второй строкой и мельче, название не теряется рядом с чипом. */}
          {isBase || !enabled ? null : statusLoading ? (
            <Skeleton className="h-3.5 w-24 rounded-full" />
          ) : (
            <LangStatusChip state={state} className="self-start whitespace-nowrap" />
          )}
        </span>
        <span className="ml-auto flex shrink-0 items-center gap-2">
          {isBase ? <BaseLangBadge stopMenu={stopMenu} /> : null}
          {isBase ? null : (
            // Добавленный язык тумблер ПОКАЗЫВАЕТ/СКРЫВАЕТ у учеников, а не
            // удаляет (удаление — в меню «⋯»). Не добавленный —
            // тумблер добавляет его.
            <span {...stopMenu} className="flex">
              {enabled ? (
                <LangVisibilitySwitch
                  courseId={courseId}
                  lang={id}
                  isPublished={isPublished}
                  disabled={switchDisabled}
                />
              ) : (
                <Switch
                  aria-label={m.langSwitch(id)}
                  checked={false}
                  disabled={switchDisabled}
                  onCheckedChange={(on) => (on ? onEnable?.() : undefined)}
                />
              )}
            </span>
          )}
          {!isBase && enabled ? (
            <span {...stopMenu} className="flex">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    aria-label={m.langActions(id)}
                    className="flex size-7 items-center justify-center rounded-lg text-muted-foreground transition hover:bg-foreground/10 hover:text-foreground"
                  >
                    <MoreHorizontal aria-hidden className="size-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {/* Скрытый язык базовым не сделать: сервер откажет. */}
                  <DisabledHint
                    inset
                    text={actionsHint ?? (isPublished ? null : m.makeBaseNeedsVisible)}
                  >
                    <DropdownMenuItem
                      disabled={actionsDisabled || !isPublished}
                      onSelect={onMakeBase}
                    >
                      {m.makeBaseItem}
                    </DropdownMenuItem>
                  </DisabledHint>
                  {onDelete ? (
                    <DisabledHint text={actionsHint} inset>
                      <DropdownMenuItem
                        className="text-destructive focus:text-destructive"
                        disabled={actionsDisabled}
                        onSelect={onDelete}
                      >
                        {m.deleteLangItem}
                      </DropdownMenuItem>
                    </DisabledHint>
                  ) : null}
                </DropdownMenuContent>
              </DropdownMenu>
            </span>
          ) : null}
        </span>
      </DropdownMenuItem>
    </>
  )
}
